'use client'

import styles from './VPadPairingModal.module.css'
import { usePadsStore } from '@/stores/usePadsStore'
import IconCross from '@/assets/img/Icon_Cross.svg'
import IconButton, { IconButtonKind, IconButtonSize } from '@/components/UI/Buttons/IconButton/IconButton'
import Step1PairedVPadImage from '@/assets/img/Image_VPadPairing_step1.png'
import Step2PairedVPadImage from '@/assets/img/Image_VPadPairing_step2.png'
import Step3PairedVPadImage from '@/assets/img/Image_VPadPairing_step3.png'
import Image from 'next/image'
import QRCode from 'react-qr-code'
import { useTranslations } from 'next-intl'
import { useEffect, useRef } from 'react'
import { FEATURES } from '@/utils/constants'
import { useModalStore } from '@/stores/useModalStore'
import { PadSessionStage, VPadStage } from '@/interfaces/virtualPad_types'
import GamepadsModal from '../GamepadsModal/GamepadsModal'
import { ModalTypes } from '@/interfaces/api/generic/api_modals_types'
import IconWifi from '@/assets/img/Icon_Wifi.svg'
import IconInfo from '@/assets/img/Icon_Info.svg'

export default function VPadPairingModal () {
  const t = useTranslations('Modals.VPadPairing')
  const closeModal = useModalStore(state => state.closeModal)
  const openModal = useModalStore(state => state.openModal)

  const vpSessionStage = usePadsStore(state => state.stage)
  const vpSessionId = usePadsStore(state => state.vpSessionId)
  const vpads = usePadsStore(state => state.vpads)

  const connectedVPads = Object.values(vpads).filter(
    vPad => vPad.stage === VPadStage.CONNECTED
  ).length

  const previousConnectedVPads = useRef(connectedVPads)

  /**
   * If the session is no longer enabled, close the modal
   */
  useEffect(() => {
    if (vpSessionStage === PadSessionStage.NONE ||
    vpSessionStage === PadSessionStage.CLOSED ||
    vpSessionId === null) {
      closeModal()
    }
  }, [vpSessionStage, vpSessionId])

  /**
   * Show the gamepads modal when a new virtual pad connects
   */
  useEffect(() => {
    if (connectedVPads > previousConnectedVPads.current) {
      openModal({
        type: ModalTypes.LATERAL,
        content: <GamepadsModal />
      })
    }
    previousConnectedVPads.current = connectedVPads
  }, [connectedVPads, openModal])

  return (
    <div className={styles.vpadPairingModal}>
      <div className={styles.closeBtnWrapper}>
        <IconButton
          icon={IconCross}
          size={IconButtonSize.M}
          kind={IconButtonKind.EMPTY}
          onClick={() => closeModal()}
        />
      </div>

      {vpSessionStage === PadSessionStage.CREATED && vpSessionId &&
      <div className={styles.contentWrapper}>
        <div className={styles.content}>
          <h1 className={`title3`}>{t('title')}</h1>

          <div className={styles.networkWrapper}>
            <div className={styles.networkIcon}><IconWifi /></div>
            <p className={`bodyTextMedium ${styles.networkContent}`}>{t('networkContent')}</p>
          </div>
        </div>

        <div className={styles.steps}>
          <div className={`${styles.stepCard} ${styles.step1}`}>
            <div className={styles.stepDescription}>
              <p className={`bodyTextLargeStrong ${styles.numStep}`}>1</p>
              <p className={`bodyTextMedium ${styles.contentStep}`}>{t('step1Content')}</p>
            </div>

            <div className={styles.stepContent}>
              <div className={styles.step1_image}>
                <Image src={Step1PairedVPadImage} alt={'Paired virtualPad'} priority />
              </div>
            </div>
          </div>

          <div className={`${styles.stepCard} ${styles.step2}`}>
            <div className={styles.stepDescription}>
              <p className={`bodyTextLargeStrong ${styles.numStep}`}>2</p>
              <p className={`bodyTextMedium ${styles.contentStep}`}>{t('step2Content')}</p>
            </div>

            <div className={styles.stepContent}>
              <div className={styles.step2_image}>
                <Image src={Step2PairedVPadImage} alt={'Paired virtualPad'} priority />
              </div>
            </div>
          </div>

          <div className={`${styles.stepCard} ${styles.step3}`}>
            <div className={styles.stepDescription}>
              <p className={`bodyTextLargeStrong ${styles.numStep}`}>3</p>
              <p className={`bodyTextMedium ${styles.contentStep}`}>{t('step3Content')}</p>
            </div>

            <div className={styles.stepContent}>
              <div className={styles.step3_content}>
                <div className={styles.QR__image}>
                  <QRCode value={vpSessionId} style={{ height: "auto", maxWidth: "100%", width: "100%" }} />
                </div>

                <div className={styles.step3_image}>
                  <Image src={Step3PairedVPadImage} alt={'Paired virtualPad'} priority />
                </div>
              </div>

              <div className={styles.QR_code}>
                <p className={`bodyTextMedium`}>{t('codeContent')}</p>
                <p className={`title3 ${styles.code}`}>{vpSessionId}</p>
              </div>
            </div>
          </div>
        </div>

        <div className={styles.priorityGamepadWrapper}>
          <IconInfo className={styles.infoIcon} />
          <p className={`bodyTextSmall ${styles.priorityGamepadContent}`}>{t('priorityGamepadContent')}</p>
        </div>

        {FEATURES.virtualPadsBeta && <p className={`bodyTextSmall ${styles.betaText}`}>{t('betaTxt')}</p>}
      </div>
      }
    </div>
  )
}